import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { App } from '../types';
import { apps as initialApps } from '../config/apps';
import { Conversation, ChatMode, generateConversationTitle, calculateConversationTokens, ConversationExport } from '../types/conversation';

export type ViewMode = 'grid' | 'list';
export type Theme = 'light' | 'dark' | 'system';

export interface Notification {
  id: string;
  title: string;
  message: string;
  type: 'info' | 'success' | 'warning' | 'error';
  timestamp: Date;
  read: boolean;
  actionText?: string;
  actionUrl?: string;
}

export interface Activity {
  id: string;
  action: string;
  appName: string;
  timestamp: Date;
  icon: string;
}

export interface AppUsageCategory {
  category: string;
  count: number;
}

export interface EngagementMetric {
  name: string;
  value: number;
  [key: string]: string | number;
}

export interface PerformanceData {
  time: string;
  cpu: number;
  memory: number;
  latency: number;
}

export interface UserPreferences {
  theme: Theme;
  viewMode: ViewMode;
  notificationsEnabled: boolean;
  compactMode: boolean;
}

export interface UserProfile {
  name: string;
  email: string;
  role: string;
  avatar?: string;
}

export interface ApiKeys {
  gemini?: string;
  openai?: string;
  anthropic?: string;
}

export interface AppState {
  // Apps
  apps: App[];
  favoriteAppIds: string[];
  recentAppIds: string[];

  // User preferences
  preferences: UserPreferences;

  // User profile
  userProfile: UserProfile;

  // API keys
  apiKeys: ApiKeys;

  // Notifications
  notifications: Notification[];

  // Activity
  activities: Activity[];

  // Command Palette
  commandPaletteOpen: boolean;

  // Stats
  aiInteractionsToday: number;
  activeSessions: number;

  // Analytics
  dailyInteractions: number[];
  appUsageByCategory: AppUsageCategory[];
  engagementMetrics: EngagementMetric[];
  performanceData: PerformanceData[];

  // Conversations
  conversations: Conversation[];
  activeConversationId: string | null;

  // Actions
  toggleFavorite: (appId: string) => void;
  addRecentApp: (appId: string) => void;
  setViewMode: (mode: ViewMode) => void;
  setTheme: (theme: Theme) => void;
  toggleCommandPalette: () => void;
  addNotification: (notification: Omit<Notification, 'id' | 'timestamp' | 'read'>) => void;
  markNotificationAsRead: (notificationId: string) => void;
  markAllNotificationsAsRead: () => void;
  clearNotifications: () => void;
  addActivity: (activity: Omit<Activity, 'id' | 'timestamp'>) => void;
  updatePreferences: (preferences: Partial<UserPreferences>) => void;
  incrementAIInteractions: () => void;
  setActiveSessions: (count: number) => void;

  // Settings actions
  updateProfile: (profile: Partial<UserProfile>) => void;
  updateApiKeys: (keys: Partial<ApiKeys>) => void;
  exportUserData: () => void;

  // Conversation actions
  createConversation: (mode?: ChatMode) => string;
  deleteConversation: (conversationId: string) => void;
  switchConversation: (conversationId: string) => void;
  updateConversation: (conversationId: string, updates: Partial<Conversation>) => void;
  togglePinConversation: (conversationId: string) => void;
  exportConversation: (conversationId: string) => void;
}

// Generate sample notifications
const generateSampleNotifications = (): Notification[] => {
  const now = new Date();
  return [
    {
      id: '1',
      title: 'Welcome to Recipe Labs!',
      message: 'Your team workspace is all set up and ready to go.',
      type: 'success',
      timestamp: new Date(now.getTime() - 3600000),
      read: false
    },
    {
      id: '2',
      title: 'New Tool Added',
      message: 'Social Media Research is now available for everyone to use.',
      type: 'info',
      timestamp: new Date(now.getTime() - 7200000),
      read: false,
      actionText: 'Try It Now',
      actionUrl: '/apps/media-scraper'
    },
    {
      id: '3',
      title: 'We Made Things Better',
      message: 'AI Toolbox has new features to help you work faster.',
      type: 'info',
      timestamp: new Date(now.getTime() - 86400000),
      read: true
    }
  ];
};

/**
 * STYLE GUIDE: Activity icons use SVG identifiers - NO emojis allowed
 * Icons map to components in src/components/icons/AppIcons.tsx
 */
const generateSampleActivities = (): Activity[] => {
  const now = new Date();
  return [
    {
      id: '1',
      action: 'Opened',
      appName: 'AI Toolbox',
      timestamp: new Date(now.getTime() - 300000),
      icon: 'flask'
    },
    {
      id: '2',
      action: 'Used',
      appName: 'Find New Clients',
      timestamp: new Date(now.getTime() - 600000),
      icon: 'target'
    },
    {
      id: '3',
      action: 'Saved',
      appName: 'Email Writer',
      timestamp: new Date(now.getTime() - 1200000),
      icon: 'mail-open'
    },
    {
      id: '4',
      action: 'Opened',
      appName: 'Strategy Builder',
      timestamp: new Date(now.getTime() - 1800000),
      icon: 'book-text'
    },
    {
      id: '5',
      action: 'Got New Features',
      appName: 'Social Media Research',
      timestamp: new Date(now.getTime() - 3600000),
      icon: 'search-scan'
    }
  ];
};

// Generate sample analytics data
const generateAnalyticsData = () => {
  // Last 7 days of interactions
  const dailyInteractions = [45, 52, 78, 95, 127, 105, 134];

  // App usage by category
  const appUsageByCategory: AppUsageCategory[] = [
    { category: 'AI Tools', count: 145 },
    { category: 'Communication', count: 98 },
    { category: 'Development', count: 76 },
    { category: 'Automation', count: 65 },
    { category: 'Analytics', count: 52 },
    { category: 'Media', count: 43 }
  ];

  // Engagement metrics
  const engagementMetrics: EngagementMetric[] = [
    { name: 'Active Users', value: 38 },
    { name: 'Power Users', value: 25 },
    { name: 'New Users', value: 15 },
    { name: 'Returning', value: 22 }
  ];

  // Performance data (last 24 hours)
  const performanceData: PerformanceData[] = Array.from({ length: 24 }, (_, i) => ({
    time: `${i.toString().padStart(2, '0')}:00`,
    cpu: Math.floor(Math.random() * 40) + 20,
    memory: Math.floor(Math.random() * 30) + 40,
    latency: Math.floor(Math.random() * 50) + 10
  }));

  return {
    dailyInteractions,
    appUsageByCategory,
    engagementMetrics,
    performanceData
  };
};

export const useAppStore = create<AppState>()(
  persist(
    (set, get) => ({
      // Initial state
      apps: initialApps,
      favoriteAppIds: [],
      recentAppIds: [],
      preferences: {
        theme: 'system',
        viewMode: 'grid',
        notificationsEnabled: true,
        compactMode: false
      },
      userProfile: {
        name: 'Recipe Labs User',
        email: 'user@recipelabs.com',
        role: 'Enterprise Admin',
        avatar: ''
      },
      apiKeys: {
        gemini: '',
        openai: '',
        anthropic: ''
      },
      notifications: generateSampleNotifications(),
      activities: generateSampleActivities(),
      commandPaletteOpen: false,
      aiInteractionsToday: 127,
      activeSessions: 3,
      conversations: [],
      activeConversationId: null,
      ...generateAnalyticsData(),

      // Actions
      toggleFavorite: (appId) =>
        set((state) => ({
          favoriteAppIds: state.favoriteAppIds.includes(appId)
            ? state.favoriteAppIds.filter((id) => id !== appId)
            : [...state.favoriteAppIds, appId]
        })),

      addRecentApp: (appId) =>
        set((state) => {
          const filtered = state.recentAppIds.filter((id) => id !== appId);
          return {
            recentAppIds: [appId, ...filtered].slice(0, 10) // Keep last 10
          };
        }),

      setViewMode: (mode) =>
        set((state) => ({
          preferences: { ...state.preferences, viewMode: mode }
        })),

      setTheme: (theme) =>
        set((state) => ({
          preferences: { ...state.preferences, theme }
        })),

      toggleCommandPalette: () =>
        set((state) => ({
          commandPaletteOpen: !state.commandPaletteOpen
        })),

      addNotification: (notification) =>
        set((state) => ({
          notifications: [
            {
              ...notification,
              id: Date.now().toString(),
              timestamp: new Date(),
              read: false
            },
            ...state.notifications
          ]
        })),

      markNotificationAsRead: (notificationId) =>
        set((state) => ({
          notifications: state.notifications.map((n) =>
            n.id === notificationId ? { ...n, read: true } : n
          )
        })),

      markAllNotificationsAsRead: () =>
        set((state) => ({
          notifications: state.notifications.map((n) => ({ ...n, read: true }))
        })),

      clearNotifications: () =>
        set({ notifications: [] }),

      addActivity: (activity) =>
        set((state) => ({
          activities: [
            {
              ...activity,
              id: Date.now().toString(),
              timestamp: new Date()
            },
            ...state.activities
          ].slice(0, 50) // Keep last 50 activities
        })),

      updatePreferences: (preferences) =>
        set((state) => ({
          preferences: { ...state.preferences, ...preferences }
        })),

      incrementAIInteractions: () =>
        set((state) => ({
          aiInteractionsToday: state.aiInteractionsToday + 1
        })),

      setActiveSessions: (count) =>
        set({ activeSessions: count }),

      // Settings actions
      updateProfile: (profile: Partial<UserProfile>) =>
        set((state) => ({
          userProfile: { ...state.userProfile, ...profile }
        })),

      updateApiKeys: (keys: Partial<ApiKeys>) =>
        set((state) => ({
          apiKeys: { ...state.apiKeys, ...keys }
        })),

      exportUserData: () => {
        const state = get();
        const exportData = {
          profile: state.userProfile,
          preferences: state.preferences,
          favoriteApps: state.favoriteAppIds,
          recentApps: state.recentAppIds,
          conversations: state.conversations,
          exportDate: new Date().toISOString(),
          version: '1.0.0'
        };

        const blob = new Blob([JSON.stringify(exportData, null, 2)], {
          type: 'application/json'
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `recipe-labs-data-${Date.now()}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      },

      // Conversation actions
      createConversation: (mode: ChatMode = 'assistant') => {
        const newConversation: Conversation = {
          id: `conv-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          title: 'New Conversation',
          mode,
          messages: [],
          createdAt: new Date(),
          updatedAt: new Date(),
          isPinned: false,
          tokenCount: 0
        };

        set((state) => ({
          conversations: [newConversation, ...state.conversations],
          activeConversationId: newConversation.id
        }));

        return newConversation.id;
      },

      deleteConversation: (conversationId: string) => {
        set((state) => {
          const newConversations = state.conversations.filter(c => c.id !== conversationId);
          const newActiveId = state.activeConversationId === conversationId
            ? (newConversations[0]?.id || null)
            : state.activeConversationId;

          return {
            conversations: newConversations,
            activeConversationId: newActiveId
          };
        });
      },

      switchConversation: (conversationId: string) => {
        set({ activeConversationId: conversationId });
      },

      updateConversation: (conversationId: string, updates: Partial<Conversation>) => {
        set((state) => ({
          conversations: state.conversations.map(conv =>
            conv.id === conversationId
              ? {
                  ...conv,
                  ...updates,
                  updatedAt: new Date(),
                  tokenCount: updates.messages
                    ? calculateConversationTokens(updates.messages)
                    : conv.tokenCount
                }
              : conv
          )
        }));
      },

      togglePinConversation: (conversationId: string) => {
        set((state) => ({
          conversations: state.conversations.map(conv =>
            conv.id === conversationId
              ? { ...conv, isPinned: !conv.isPinned }
              : conv
          )
        }));
      },

      exportConversation: (conversationId: string) => {
        const state = get();
        const conversation = state.conversations.find(c => c.id === conversationId);

        if (!conversation) {
          console.error('Conversation not found');
          return;
        }

        const exportData: ConversationExport = {
          conversation,
          exportedAt: new Date(),
          version: '1.0.0'
        };

        const blob = new Blob([JSON.stringify(exportData, null, 2)], {
          type: 'application/json'
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `conversation-${conversation.title.replace(/\s+/g, '-').toLowerCase()}-${Date.now()}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
    }),
    {
      name: 'recipe-labs-storage',
      partialize: (state) => ({
        favoriteAppIds: state.favoriteAppIds,
        recentAppIds: state.recentAppIds,
        preferences: state.preferences,
        userProfile: state.userProfile,
        apiKeys: state.apiKeys,
        aiInteractionsToday: state.aiInteractionsToday,
        activeSessions: state.activeSessions,
        conversations: state.conversations,
        activeConversationId: state.activeConversationId
      })
    }
  )
);
